import { CampaignOutlined } from "@mui/icons-material";
import { Card, CardContent, Grid, LinearProgress } from "@mui/material";
import CampaignStatusChip from "./campaigns/CampaignStatusChip";
import WidgedHeader from "./WidgedHeader";
import WidgetEmptyState from "./WidgetEmptyState";

type UpcomingDatesProps = {
  campaigns: {
    id: number;
    title: string;
    state: string;
    startDate?: Date;
    endDate?: Date;
  }[];
  /** Badge total; the list holds only the newest four. Falls back to the list length. */
  totalCount?: number;
};

const calcDaysAway = (endDate?: Date): number | null => {
  if (!endDate) return null;
  const diff = endDate.getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const calcProgress = (startDate?: Date, endDate?: Date): number => {
  if (!startDate || !endDate) return 0;
  const now = Date.now();
  const start = startDate.getTime();
  const end = endDate.getTime();
  return Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
};

const RecentCampaign = ({ campaigns, totalCount }: UpcomingDatesProps) => {
  return (
    <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex", flexDirection: "column" }}>
      <WidgedHeader
        title="Recent Campaigns"
        count={totalCount ?? campaigns.length}
        viewAllHref="/campaigns"
      />
      <Card
        sx={{ flex: 1, minHeight: 300, border: "1px solid #e5e5e5", boxShadow: "none" }}
        className="rounded-xl"
      >
        <CardContent className="py-2" sx={{ height: "100%" }}>
          {campaigns.length === 0 && (
            <WidgetEmptyState
              icon={<CampaignOutlined />}
              title="No campaigns yet"
              subtitle="Campaigns you create will show up here with their progress."
            />
          )}
          <ul className="pl-4">
            {campaigns.map((campaign, idx) => {
              if (idx >= 4) return null;
              const daysAway = calcDaysAway(campaign.endDate);
              const progress = calcProgress(campaign.startDate, campaign.endDate);
              return (
                <li key={campaign.id} className="my-4">
                  <div className="flex flex-row items-start justify-between">
                    <div className="flex-1 mr-4">
                      <p className="font-semibold">{campaign.title}</p>
                      <CampaignStatusChip status={campaign.state} size="sm" showDot={false} />
                    </div>
                    {daysAway !== null && daysAway > 0 && (
                      <div className="text-black font-semibold text-sm">
                        {daysAway} day{daysAway !== 1 ? "s" : ""} left
                      </div>
                    )}
                  </div>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      mt: 1,
                      borderRadius: 4,
                      backgroundColor: "var(--color-chip-completed)",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "var(--color-primary-active)",
                      },
                    }}
                    aria-label="Campaign progress"
                  />
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default RecentCampaign;
